import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './RegionsPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');
const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Bolgeler',
    pageDescription: 'Sistem yonetimi icin bolgesel siniflandirmalar.',
    newRegion: 'Yeni bolge',
    region: 'Bolge',
    code: 'Kod',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz bolge tanimi bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    active: 'Aktif',
    passive: 'Pasif',
    createRegionAria: 'Yeni bolge olustur',
    editRegionAria: 'Bolgeyi duzenle',
    createRegionTitle: 'Yeni bolge olustur',
    editRegionTitle: 'Bolgeyi duzenle',
    nameCodeRequired: 'Ad ve kod zorunludur.',
    regionsLoadError: 'Bolgeler yuklenemedi.',
    regionsLoadErrorDetail: 'Bolgeler yuklenirken hata olustu.',
    saveFailed: 'Islem basarisiz oldu.',
    operationError: 'Islem sirasinda hata olustu.',
    regionCreated: 'Bolge olusturuldu.',
    regionUpdated: 'Bolge guncellendi.',
    deleteConfirm: 'Bu bolgeyi silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    deleteError: 'Silme sirasinda hata olustu.',
    regionDeleted: 'Bolge silindi.'
  },
  'en-US': {
    pageTitle: 'Regions',
    pageDescription: 'Regional classifications for system management.',
    newRegion: 'New region',
    region: 'Region',
    code: 'Code',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No region definition found yet.',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    active: 'Active',
    passive: 'Passive',
    createRegionAria: 'Create new region',
    editRegionAria: 'Edit region',
    createRegionTitle: 'Create new region',
    editRegionTitle: 'Edit region',
    nameCodeRequired: 'Name and code are required.',
    regionsLoadError: 'Regions could not be loaded.',
    regionsLoadErrorDetail: 'An error occurred while loading regions.',
    saveFailed: 'Operation failed.',
    operationError: 'An error occurred during operation.',
    regionCreated: 'Region created.',
    regionUpdated: 'Region updated.',
    deleteConfirm: 'Are you sure you want to delete this region?',
    deleteFailed: 'Delete operation failed.',
    deleteError: 'An error occurred during delete.',
    regionDeleted: 'Region deleted.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const createFormState = () => ({
  id: null,
  name: '',
  code: '',
  status: 'Aktif'
});

function RegionsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEditMode = Boolean(formState.id);

  const statusLabel = useCallback((value) => (value === 'Aktif' ? t('active') : t('passive')), [t]);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const loadRegions = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/regions`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('regionsLoadError'));
      setRegions(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('regionsLoadErrorDetail'));
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, t]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const preparePayload = useMemo(() => () => ({
    name: formState.name.trim(),
    code: formState.code.trim(),
    status: formState.status
  }), [formState.code, formState.name, formState.status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.name || !payload.code) {
      setFormError(t('nameCodeRequired'));
      setSaving(false);
      return;
    }

    const method = isEditMode ? 'PUT' : 'POST';
    const url = `${API_BASE_URL}/api/regions${isEditMode ? `/${formState.id}` : ''}`;

    try {
      const response = await fetch(url, {
        method,
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('saveFailed'));
      }

      setSuccess(isEditMode ? t('regionUpdated') : t('regionCreated'));
      closeModal();
      await loadRegions();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (region) => {
    setFormState({
      id: region.id,
      name: region.name || '',
      code: region.code || '',
      status: region.status || 'Aktif'
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (regionId) => {
    if (!window.confirm(t('deleteConfirm'))) return;

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/regions/${regionId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }

      setSuccess(t('regionDeleted'));
      if (formState.id === regionId) closeModal();
      await loadRegions();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card regions-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            {t('newRegion')}
          </button>
        }
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="regions-grid">
        <section className="regions-table-panel">
          <div className="table-wrapper">
            <table className="regions-table">
              <thead>
                <tr>
                  <th>{t('region')}</th>
                  <th>{t('code')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{t('loading')}</td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{listError}</td>
                  </tr>
                ) : regions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{t('noRecords')}</td>
                  </tr>
                ) : (
                  regions.map((region) => (
                    <tr key={region.id || region.code}>
                      <td>{region.name}</td>
                      <td>{region.code}</td>
                      <td>
                        <span className={region.status === 'Aktif' ? 'badge badge-active' : 'badge'}>{statusLabel(region.status)}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(region)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-delete"
                          onClick={() => handleDelete(region.id)}
                          aria-label={t('delete')}
                          title={t('delete')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editRegionTitle') : t('createRegionTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="name">{t('region')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder={localeKey === 'en-US' ? 'Marmara Region' : 'Marmara'}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="code">{t('code')}</label>
              <input
                id="code"
                name="code"
                value={formState.code}
                onChange={(event) => setFormState((current) => ({ ...current, code: event.target.value }))}
                placeholder="TR-34"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {statusLabel(option)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default RegionsPage;
